import { Loading } from '@workwhile/ui'
import React from 'react'
import { X } from 'lucide-react'
import { CommandPaletteOption, EmptyState } from './CommandPalette_Utils'
import { EntryItem } from './EntryItem'
import _ from 'lodash'

type SectionProps<T extends CommandPaletteOption> = {
  title: string
  items: T[]
  isLoading?: boolean
  searchValue: string
  selectedItem: CommandPaletteOption | null
  onItemClick?: (item: T) => void
  onItemHover: (item: T) => void
  renderItem: (
    item: T,
    searchValue: string
  ) => {
    title: string | React.ReactNode
    description: string | React.ReactNode
  }
  emptyStateMessage: string
  hideBottomBorder?: boolean
  onClearHistory: () => void
  hide: boolean
  getItemRef?: (
    item: CommandPaletteOption
  ) => React.RefObject<HTMLDivElement | null> | null
}

export function Section<T extends CommandPaletteOption>({
  title,
  items,
  isLoading = false,
  searchValue,
  selectedItem,
  onItemClick,
  onItemHover,
  renderItem,
  emptyStateMessage,
  hideBottomBorder = false,
  onClearHistory,
  hide,
  getItemRef,
}: SectionProps<T>) {
  const isSelected = (item: CommandPaletteOption) => {
    if (!selectedItem) return false
    return _.isEqual(item, selectedItem)
  }

  return (
    <div
      style={{
        flex: '0 0 auto',
        display: hide ? 'none' : 'flex',
        flexDirection: 'column',
        borderBottom: hideBottomBorder ? 'none' : '1px solid #e5e7eb',
      }}
    >
      <div
        style={{
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px 4px',
            marginBottom: '4px',
          }}
        >
          <div
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#6b7280',
              letterSpacing: '0.5px',
            }}
          >
            {searchValue.trim() ? title : `${title} (HISTORY)`}
          </div>
          {!searchValue.trim() && items.length > 0 ? (
            <button
              onClick={onClearHistory}
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '10px',
              }}
            >
              <X size={12} style={{ marginRight: '4px' }} />
              Clear
            </button>
          ) : null}
        </div>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '8px',
            }}
          >
            <Loading type="button" />
          </div>
        ) : items.length > 0 ? (
          items.map((item) => {
            const { title: itemTitle, description } = renderItem(
              item,
              searchValue
            )
            const itemRef = getItemRef?.(item) || null
            return (
              <EntryItem
                key={item.id}
                ref={itemRef}
                title={itemTitle}
                description={description}
                isSelected={isSelected(item)}
                onClick={onItemClick ? () => onItemClick(item) : undefined}
                onMouseEnter={() => onItemHover(item)}
              />
            )
          })
        ) : searchValue.trim() ? (
          <EmptyState>{emptyStateMessage}</EmptyState>
        ) : (
          <EmptyState>No history available</EmptyState>
        )}
      </div>
    </div>
  )
}
